<template>
  <div class="home-dashboard">
    <div class="dashboard-header mb-4">
      <h2>Dashboard Overview</h2>
      <div class="dashboard-date">
        {{ currentDate }}
      </div>
    </div>

    <div v-if="loading" class="dashboard-loading">
      <i class="fas fa-spinner fa-spin fa-2x"></i>
      <span>Loading dashboard...</span>
    </div>

    <template v-else>
    <div class="row mb-4">
      <div class="col-md-3 col-sm-6 mb-3">
        <div class="stats-card clickable-card" @click="$router.push('/pages/clients')">
          <div class="stats-icon">
            <i class="fa fa-users text-primary"></i>
          </div>
          <div class="stats-content">
            <h3>{{ stats.totalClients }}</h3>
            <p>Total Clients</p>
            <small class="text-success">
              {{ stats.newClientsThisMonth }} new this month
            </small>
          </div>
        </div>
      </div>

      <div class="col-md-3 col-sm-6 mb-3">
        <div class="stats-card clickable-card" @click="$router.push('/pages/caregivers')">
          <div class="stats-icon">
            <i class="fa fa-user-nurse text-info"></i>
          </div>
          <div class="stats-content">
            <h3>{{ stats.totalCaregivers }}</h3>
            <p>Total Caregivers</p>
            <small class="text-warning">
              {{ stats.scheduledToday }} scheduled today
            </small>
          </div>
        </div>
      </div>

      <div class="col-md-3 col-sm-6 mb-3">
        <div class="stats-card clickable-card" @click="$router.push('/pages/shifts')">
          <div class="stats-icon">
            <i class="fa fa-calendar-check text-success"></i>
          </div>
          <div class="stats-content">
            <h3>{{ stats.todayShifts }}</h3>
            <p>Today's Shifts</p>
            <small class="text-info">
              {{ stats.completedShifts }}% completed
            </small>
          </div>
        </div>
      </div>

      <div class="col-md-3 col-sm-6 mb-3">
        <div class="stats-card clickable-card" @click="viewShiftApprovals">
          <div class="stats-icon">
            <i class="fa fa-exclamation-triangle text-danger"></i>
          </div>
          <div class="stats-content">
            <h3>{{ stats.pendingApprovals }}</h3>
            <p>Pending Approvals</p>
            <small class="text-danger">
              {{ stats.overdueApprovals }} overdue
            </small>
          </div>
        </div>
      </div>
    </div>

    <!-- General Alerts (overlap warnings, mileage caps, etc.) -->
    <div v-if="generalNotifications.length > 0" class="row mb-4">
      <div class="col-12">
        <div class="notification-banner">
          <h5 class="mb-3">
            <i class="fa fa-bell text-warning"></i>
            Alerts ({{ generalNotifications.length }})
          </h5>
          <div class="notification-list">
            <div
              v-for="n in generalNotifications.slice(0, 8)"
              :key="n.id"
              class="notification-item"
              :class="n.type === 'warning' || n.type === 'error' ? 'notification-warning' : 'notification-info'"
            >
              <div class="notification-content">
                <strong>{{ n.title }}</strong>
                <span class="notification-message">{{ n.message }}</span>
              </div>
              <div class="request-actions">
                <button
                  v-if="n.action_url"
                  class="btn btn-sm btn-primary"
                  @click="openGeneralNotification(n)"
                >
                  <i class="fa fa-eye"></i> View
                </button>
                <button
                  class="btn btn-sm btn-secondary"
                  @click="dismissGeneralNotification(n)"
                >
                  Mark read
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Schedule Change Requests Section (Admin Only) -->
    <div v-if="isAdmin" class="row mb-4">
      <div class="col-12">
        <div class="notification-banner schedule-requests-banner">
          <h5 class="mb-3">
            <i class="fa fa-calendar-alt text-info"></i>
            Pending Work Schedule Requests ({{ pendingScheduleRequests.length }})
          </h5>
          <div class="request-list" v-if="pendingScheduleRequests.length > 0">
            <div
              v-for="request in pendingScheduleRequests.slice(0, 5)"
              :key="request.id"
              class="request-item clickable"
              @click="viewScheduleRequest(request)"
            >
              <div class="request-content">
                <strong>
                  {{ request.requester ? `${request.requester.first_name} ${request.requester.last_name}` : 'Unknown User' }}
                </strong>
                <span class="request-details">
                  Effective Date: {{ formatDate(request.effective_date) }}
                  <span v-if="request.reason" class="text-muted"> - {{ truncateReason(request.reason) }}</span>
                </span>
              </div>
              <div class="request-actions">
                <button
                  v-if="canReviewRequest(request)"
                  @click.stop="viewScheduleRequest(request)"
                  class="btn btn-sm btn-primary"
                >
                  <i class="fa fa-eye"></i> Review
                </button>
              </div>
            </div>
          </div>
          <div v-else class="text-center text-muted py-3">
            <i class="fa fa-check-circle fa-2x mb-2"></i>
            <p>No pending schedule change requests</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Employee Compliance Section (Admin Only) -->
    <div v-if="isAdmin" class="row mb-4">
      <div class="col-12">
        <div class="notification-banner compliance-banner">
          <h5 class="mb-3">
            <i class="fas fa-clipboard-check text-warning"></i>
            Employee Compliance ({{ totalComplianceItems }})
          </h5>

          <!-- Pending Approvals -->
          <div v-if="groupedPendingApprovals.length > 0" class="compliance-category">
            <div class="compliance-category-header compliance-pending-header">
              <i class="fas fa-file-signature"></i>
              Pending Review ({{ complianceSummary.pendingApprovals.count }})
            </div>
            <div
              v-for="group in groupedPendingApprovals"
              :key="'pending-' + group.caregiverId"
              class="request-item clickable"
              @click="$router.push(`/pages/caregivers/${group.caregiverId}?tab=compliance`)"
            >
              <div class="request-content">
                <strong>{{ group.caregiverName }}</strong>
                <span class="request-details">
                  {{ group.items.length }} document{{ group.items.length > 1 ? 's' : '' }} awaiting review
                </span>
              </div>
              <div class="request-actions">
                <button
                  @click.stop="$router.push(`/pages/caregivers/${group.caregiverId}?tab=compliance`)"
                  class="btn btn-sm btn-primary"
                >
                  <i class="fa fa-eye"></i> Review
                </button>
              </div>
            </div>
          </div>

          <!-- Expired Documents -->
          <div v-if="groupedExpiredItems.length > 0" class="compliance-category">
            <div class="compliance-category-header compliance-expired-header">
              <i class="fas fa-exclamation-triangle"></i>
              Expired ({{ complianceSummary.expiredItems.count }})
            </div>
            <div
              v-for="group in groupedExpiredItems"
              :key="'expired-' + group.caregiverId"
              class="request-item clickable"
              @click="$router.push(`/pages/caregivers/${group.caregiverId}?tab=compliance`)"
            >
              <div class="request-content">
                <strong>{{ group.caregiverName }}</strong>
                <span class="request-details">
                  {{ group.items.length }} expired document{{ group.items.length > 1 ? 's' : '' }}
                </span>
              </div>
              <div class="request-actions">
                <button
                  @click.stop="$router.push(`/pages/caregivers/${group.caregiverId}?tab=compliance`)"
                  class="btn btn-sm btn-danger"
                >
                  <i class="fa fa-eye"></i> View
                </button>
              </div>
            </div>
          </div>

          <!-- Expiring Soon -->
          <div v-if="groupedExpiringSoon.length > 0" class="compliance-category">
            <div class="compliance-category-header compliance-expiring-header">
              <i class="fas fa-clock"></i>
              Expiring Soon ({{ complianceSummary.expiringSoon.count }})
            </div>
            <div
              v-for="group in groupedExpiringSoon"
              :key="'expiring-' + group.caregiverId"
              class="request-item clickable"
              @click="$router.push(`/pages/caregivers/${group.caregiverId}?tab=compliance`)"
            >
              <div class="request-content">
                <strong>{{ group.caregiverName }}</strong>
                <span class="request-details">
                  {{ group.items.length }} document{{ group.items.length > 1 ? 's' : '' }} expiring soon
                </span>
              </div>
              <div class="request-actions">
                <button
                  @click.stop="$router.push(`/pages/caregivers/${group.caregiverId}?tab=compliance`)"
                  class="btn btn-sm btn-warning"
                >
                  <i class="fa fa-eye"></i> View
                </button>
              </div>
            </div>
          </div>

          <div v-if="totalComplianceItems === 0" class="text-center text-muted py-3">
            <i class="fa fa-check-circle fa-2x mb-2"></i>
            <p>All employee compliance documents are up to date</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Email Authorization Tracking Dashboard (Admin Only) -->
    <div v-if="isAdmin" class="row mb-4">
      <div class="col-12">
        <h5 class="section-header d-flex align-items-center justify-content-between">
          <span>
            <i class="fas fa-envelope"></i>
            Email Authorization Tracking
          </span>
          <el-switch
            :value="!hideEmailAuthTracking"
            @change="toggleEmailAuthTracking"
            active-text="Show"
            inactive-text="Hide"
            style="margin-left: 12px;"
          />
        </h5>
        <EmailAuthorizationDashboard
          v-if="!hideEmailAuthTracking"
          ref="emailAuthDashboard"
          @open-auth-modal="openEmailAuthModal"
        />
      </div>
    </div>

    <EmailAuthorizationModal
      v-model="showEmailAuthModal"
      :notification="selectedAuthNotification"
      @approved="handleAuthAlertApproved"
      @rejected="handleAuthAlertRejected"
      @regenerated="handleAuthAlertRegenerated"
    />
    </template>

  </div>
</template>

<script>
import { shiftsService } from "@/serviceHandlers/shiftsService";
import { clientsService } from "@/serviceHandlers/clientsService";
import { caregiversService } from "@/serviceHandlers/caregiversService";
import { notificationsService } from "@/serviceHandlers/notificationsService";
import { scheduleChangeRequestsService } from "@/serviceHandlers/scheduleChangeRequestsService";
import { caregiverComplianceService } from "src/serviceHandlers/caregiverComplianceService";
import { usersService } from "@/serviceHandlers/usersService";
import { useNotificationsStore } from "@/stores/notificationsStore";
import { useLoginStore } from "@/stores/loginStore";
import EmailAuthorizationModal from "@/components/EmailAuthorizationModal.vue";
import EmailAuthorizationDashboard from "@/components/EmailAuthorizationDashboard.vue";

export default {
  name: "HomeDashboard",
  components: {
    EmailAuthorizationModal,
    EmailAuthorizationDashboard,
  },
  data() {
    return {
      loading: false,
      showEmailAuthModal: false,
      selectedAuthNotification: null,
      stats: {
        totalClients: 0,
        totalCaregivers: 0,
        todayShifts: 0,
        completedShifts: 0,
        pendingApprovals: 0,
        overdueApprovals: 0,
        newClientsThisMonth: 0,
        scheduledToday: 0,
      },
      notificationsStore: null,
      loginStore: null,
      todaySchedule: [],
      clients: [],
      caregivers: [],
      pendingScheduleRequests: [],
      complianceSummary: {
        pendingApprovals: { count: 0, items: [] },
        expiredItems: { count: 0, items: [] },
        expiringSoon: { count: 0, items: [] },
      },
    };
  },
  computed: {
    currentDate() {
      return new Date().toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    isAdmin() {
      return this.loginStore?.user?.user_type === 'Admin';
    },

    // Unread generic notifications for the signed-in user (overlap warnings,
    // mileage cap alerts, etc.) — loaded by notificationsStore.loadNotifications()
    generalNotifications() {
      // Email-auth alert rows render in their own dashboard section already.
      return (this.notificationsStore?.notifications || []).filter(
        (n) => n.notification_type !== 'low_authorization_hours',
      );
    },

    hideEmailAuthTracking() {
      return this.loginStore?.user?.hide_email_auth_tracking || false;
    },

    totalComplianceItems() {
      return this.complianceSummary.pendingApprovals.count +
        this.complianceSummary.expiredItems.count +
        this.complianceSummary.expiringSoon.count;
    },

    groupedPendingApprovals() {
      return this.groupByCaregiverId(this.complianceSummary.pendingApprovals.items);
    },

    groupedExpiredItems() {
      return this.groupByCaregiverId(this.complianceSummary.expiredItems.items);
    },

    groupedExpiringSoon() {
      return this.groupByCaregiverId(this.complianceSummary.expiringSoon.items);
    },

    currentUserId() {
      return this.loginStore?.user?.id;
    },
  },
  async mounted() {
    this.notificationsStore = useNotificationsStore();
    this.loginStore = useLoginStore();
    await this.loadDashboardData();
    this.startAutoRefresh();
  },
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
  methods: {
    async loadDashboardData(background = false) {
      // Background refreshes swap data in place. Flipping `loading` here
      // would v-if-unmount the entire dashboard (including an open
      // authorization-request modal) and remount it — which reads as the
      // page reloading out from under the user.
      if (!background) this.loading = true;
      try {
        const todayShifts = await this.getTodayShifts();

        const promises = [
          this.loadStats(todayShifts),
          this.notificationsStore.loadNotifications(),
          this.loadTodaySchedule(todayShifts),
        ];

        // Load schedule change requests and compliance summary if user is admin
        if (this.isAdmin) {
          promises.push(this.loadScheduleChangeRequests());
          promises.push(this.loadComplianceSummary());
        }

        await Promise.all(promises);
      } catch (error) {
        // A failed silent poll shouldn't toast an error every 5 minutes
        if (!background) {
          this.$notifyError("Error loading dashboard data: " + error);
        }
      } finally {
        if (!background) this.loading = false;
      }
    },

    async loadStats(todayShifts = null) {
      const [clients, caregivers, shifts] = await Promise.all([
        clientsService.list({ slim: true }),
        caregiversService.list(),
        todayShifts ? Promise.resolve(todayShifts) : this.getTodayShifts(),
      ]);

      this.clients = clients;
      this.caregivers = caregivers;

      // Filter for active clients only
      const activeClients = clients.filter(c => c.active === 1 || c.active === true);

      // Calculate new clients this month
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const newClientsThisMonth = activeClients.filter(c => {
        if (!c.createdAt) return false;
        const createdDate = new Date(c.createdAt);
        return createdDate >= firstDayOfMonth;
      }).length;

      // Calculate overdue approvals (shifts clocked out more than 48 hours ago but not approved)
      const twoDaysAgo = new Date();
      twoDaysAgo.setHours(twoDaysAgo.getHours() - 48);
      const overdueApprovals = shifts.filter((s) => {
        if (!s.clockOut || s.shift_status === "approved") return false;
        const clockOutDate = new Date(s.clockOut);
        return clockOutDate < twoDaysAgo;
      }).length;

      // Count unique caregivers scheduled today (assigned to at least one shift)
      const uniqueScheduledCaregiverIds = new Set();
      shifts.forEach(shift => {
        if (shift.caregivers && shift.caregivers.length > 0) {
          shift.caregivers.forEach(caregiver => {
            uniqueScheduledCaregiverIds.add(caregiver.id);
          });
        }
      });

      this.stats = {
        totalClients: activeClients.length,
        totalCaregivers: caregivers.length,
        todayShifts: shifts.length,
        completedShifts:
          Math.round(
            (shifts.filter((s) => s.clockOut).length / shifts.length) * 100,
          ) || 0,
        pendingApprovals: shifts.filter(
          (s) => s.clockOut && s.shift_status !== "approved",
        ).length,
        overdueApprovals: overdueApprovals,
        newClientsThisMonth: newClientsThisMonth,
        scheduledToday: uniqueScheduledCaregiverIds.size,
      };

      // Update the notifications store with pending approvals
      this.notificationsStore.updatePendingShiftApprovals(this.stats.pendingApprovals);
    },

    async getTodayShifts() {
      // Query the LOCAL day's real boundaries as UTC instants. Building the
      // window from toISOString's date is doubly wrong: after ~6pm Denver the
      // UTC date is tomorrow, and T00:00Z-T23:59Z is the UTC day, so evening
      // local shifts fall outside "today" either way.
      const startOfDay = new Date();
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date();
      endOfDay.setHours(23, 59, 59, 999);
      const params = {
        startDate: startOfDay.toISOString(),
        endDate: endOfDay.toISOString(),
        userType: "Admin",
        userId: 1,
        perPage: 1000,
      };
      const response = await shiftsService.getByRole(params);
      return response.shifts || [];
    },



    async loadTodaySchedule(todayShifts = null) {
      this.todaySchedule = todayShifts || (await this.getTodayShifts());
    },


    startAutoRefresh() {
      this.refreshInterval = setInterval(
        () => {
          this.loadDashboardData(true);
        },
        5 * 60 * 1000,
      );
    },

    formatDate(date) {
      if (!date) return '';
      // Parse YYYY-MM-DD as local time to avoid UTC timezone shift
      const [y, m, d] = date.split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString();
    },

    async openGeneralNotification(notification) {
      await this.notificationsStore.markNotificationAsRead(notification.id);
      if (notification.action_url) {
        this.$router.push(notification.action_url).catch(() => {});
      }
    },

    async dismissGeneralNotification(notification) {
      await this.notificationsStore.markNotificationAsRead(notification.id);
    },

    formatTime(time) {
      if (!time) return "";
      const [hours, minutes] = time.split(":");
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    },

    formatClient(shift) {
      return shift.clients && shift.clients.length > 0
        ? shift.clients[0].name
        : "Unassigned";
    },

    formatCaregiver(shift) {
      return shift.caregivers && shift.caregivers.length > 0
        ? shift.caregivers[0].name
        : "Unassigned";
    },

    formatStatus(status) {
      const statusMap = {
        scheduled: "Scheduled",
        "clocked-in": "In Progress",
        "clocked-out": "Completed",
        approved: "Approved",
      };
      return statusMap[status] || status;
    },

    getStatusClass(status) {
      const classMap = {
        scheduled: "badge-info",
        "clocked-in": "badge-success",
        "clocked-out": "badge-warning",
        approved: "badge-primary",
      };
      return classMap[status] || "badge-secondary";
    },


    generateReport() {
      this.$notifyInfo("Report generation feature coming soon!");
    },

    viewShiftApprovals() {
      this.$router.push('/pages/shifts?status=pending-approval');
    },

    async toggleEmailAuthTracking(showValue) {
      const hideValue = !showValue;
      try {
        await usersService.updatePreferences({ hide_email_auth_tracking: hideValue });
        this.$set(this.loginStore.user, 'hide_email_auth_tracking', hideValue);
      } catch (e) {
        this.$notifyError('Failed to save preference');
      }
    },

    openEmailAuthModal(notification) {
      this.selectedAuthNotification = notification;
      this.showEmailAuthModal = true;
    },

    async handleAuthAlertApproved(notification) {
      await this.notificationsStore.loadNotifications();
      this.$refs.emailAuthDashboard?.loadDashboardData();
    },

    async handleAuthAlertRejected(notification) {
      await this.notificationsStore.loadNotifications();
      this.$refs.emailAuthDashboard?.loadDashboardData();
    },

    async handleAuthAlertRegenerated(updatedNotification) {
      // Update the notification in place to show the new AI content immediately
      if (updatedNotification && this.selectedAuthNotification?.id === updatedNotification.id) {
        this.selectedAuthNotification = updatedNotification;
      }
      // Also reload notifications to keep everything in sync
      await this.notificationsStore.loadNotifications();
    },

    groupByCaregiverId(items) {
      const map = {};
      for (const item of items) {
        if (!map[item.caregiverId]) {
          map[item.caregiverId] = {
            caregiverId: item.caregiverId,
            caregiverName: item.caregiverName,
            items: [],
          };
        }
        map[item.caregiverId].items.push(item);
      }
      return Object.values(map);
    },

    async loadComplianceSummary() {
      try {
        const response = await caregiverComplianceService.getDashboardSummary();
        if (response) {
          this.complianceSummary = response;
          // Push count to notifications store so it doesn't need to fetch separately
          this.notificationsStore.pendingComplianceItems =
            (response.pendingApprovals?.count || 0) +
            (response.expiredItems?.count || 0) +
            (response.expiringSoon?.count || 0);
        }
      } catch (error) {
        // Silently fail - compliance section is supplementary
      }
    },

    // Schedule Change Request Methods
    async loadScheduleChangeRequests() {
      try {
        const response = await scheduleChangeRequestsService.getPendingRequests();
        // Handle paginated response
        let requests;
        if (response && response.data) {
          requests = response.data || [];
        } else {
          requests = response || [];
        }

        // Parse requested_schedule if it comes back as a JSON string
        this.pendingScheduleRequests = requests.map(req => {
          if (typeof req.requested_schedule === 'string') {
            req.requested_schedule = JSON.parse(req.requested_schedule);
          }
          return req;
        });

        // Push count to notifications store so it doesn't need to fetch separately
        const total = response?.pagination?.total ?? this.pendingScheduleRequests.length;
        this.notificationsStore.pendingScheduleChangeRequests = total;
      } catch (error) {
        // Only show error if it's not a 404 (no requests found)
        this.pendingScheduleRequests = [];
      }
    },

    viewScheduleRequest(request) {
      const name = request.requester
        ? `${request.requester.first_name} ${request.requester.last_name}`
        : undefined;
      this.$router.push({
        path: `/pages/caregivers/${request.id_users}`,
        query: { tab: 'work-schedule', view: 'requested', name },
      });
    },

    canReviewRequest(request) {
      // Admins can review pending requests, but not their own
      return this.isAdmin && request.status === 'pending' && request.id_users !== this.currentUserId;
    },

    truncateReason(reason) {
      if (!reason) return '';
      return reason.length > 60 ? reason.substring(0, 60) + '...' : reason;
    },

  },
};
</script>

<style scoped>
.home-dashboard {
  padding: 1rem;
  color: #e0e0e0;
}

.dashboard-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 2rem;
  color: #888;
}

.dashboard-loading i {
  color: #6ba6cd;
}

.home-dashboard h2, .home-dashboard h3, .home-dashboard h5 {
  color: #fff;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-wrap: wrap;
  gap: 1rem;
}

.dashboard-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-change-password {
  background: rgba(23, 162, 184, 0.2);
  color: #17a2b8;
  border: 1px solid rgba(23, 162, 184, 0.4);
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-change-password:hover {
  background: rgba(23, 162, 184, 0.3);
  border-color: rgba(23, 162, 184, 0.6);
  color: #17a2b8;
}

.btn-change-password i {
  font-size: 0.875rem;
}

.dashboard-date {
  color: #b3a6a6;
  font-size: 0.9rem;
}

.notification-banner {
  background: rgba(37, 55, 65, 0.9);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  border-left: 4px solid #ffc107;
  padding: 1rem;
  border-radius: 0.25rem;
}

.unified-notifications {
  border-left: 4px solid #17a2b8;
}

.schedule-requests-banner {
  border-left: 4px solid #17a2b8;
}

.request-list {
  margin-top: 1rem;
}

.request-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  margin-bottom: 0.5rem;
  border-radius: 0.25rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: background-color 0.2s;
}

.request-item.clickable {
  cursor: pointer;
}

.request-item.clickable:hover {
  background: rgba(255, 255, 255, 0.1);
}

.request-content {
  flex: 1;
  color: #e0e0e0;
}

.request-content strong {
  color: #ffffff;
  font-weight: bold;
  display: block;
  margin-bottom: 0.25rem;
}

.request-details {
  display: block;
  color: #b3a6a6;
  font-size: 0.9rem;
  margin-top: 0.25rem;
}

.request-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.request-actions .btn {
  min-width: 90px;
}

.notification-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  margin-bottom: 0.5rem;
  border-radius: 0.25rem;
}

/*noinspection CssUnusedSymbol */
.notification-warning {
  background: rgba(255, 193, 7, 0.1);
  border-left: 3px solid #ffc107;
}

/*noinspection CssUnusedSymbol */
.notification-info {
  background: rgba(23, 162, 184, 0.1);
  border-left: 3px solid #17a2b8;
}

/*noinspection CssUnusedSymbol */
.notification-shift-approval {
  background: rgba(13, 110, 253, 0.1);
  border-left: 3px solid #0d6efd;
}

.auth-alert-banner {
  background: rgba(220, 53, 69, 0.1);
  border-left: 4px solid #dc3545;
}

.auth-alert-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  margin-bottom: 0.5rem;
  border-radius: 0.25rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.auth-alert-item .notification-content {
  flex: 1;
  color: #e0e0e0;
}

.auth-alert-item .notification-content strong {
  color: #ffffff;
  font-weight: bold;
}

.auth-alert-item .notification-message {
  display: block;
  color: #b3a6a6;
  font-size: 0.9rem;
  margin-top: 0.25rem;
}

.auth-alert-item .notification-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
}

.auth-alert-item .notification-actions .btn {
  min-width: 110px;
}

.auth-alert-item .btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.auth-alert-item.clickable {
  cursor: pointer;
  transition: background-color 0.2s;
}

.auth-alert-item.clickable:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.notification-list {
  max-height: 400px;
  overflow-y: auto;
}

.notification-content {
  flex: 1;
  color: #e0e0e0;
}

.notification-content strong {
  color: #fff;
}

.notification-message {
  display: block;
  color: #b3a6a6;
  font-size: 0.9rem;
}

.stats-card {
  background: rgba(37, 55, 65, 0.9);
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  height: 100%;
  transition: all 0.2s ease;
}

.clickable-card {
  cursor: pointer;
}

.clickable-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.4);
  border-color: rgba(255, 255, 255, 0.2);
  background: rgba(37, 55, 65, 0.95);
}

.stats-icon {
  font-size: 2rem;
  margin-right: calc(1rem + 20px);
}

.stats-content h3 {
  margin: 0;
  font-size: 2rem;
  font-weight: bold;
  color: #fff;
}

.stats-content p {
  margin: 0.25rem 0;
  color: #b3a6a6;
}

.dashboard-card {
  background: rgba(37, 55, 65, 0.9);
  border-radius: 0.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  height: 100%;
}

.card-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-body {
  padding: 1.5rem;
  color: #e0e0e0;
}


.schedule-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.schedule-time {
  font-weight: bold;
  color: #e0e0e0;
  min-width: 80px;
}

.schedule-details {
  flex: 1;
  margin-left: 1rem;
  color: #e0e0e0;
}



.badge {
  font-size: 0.75em;
  padding: 0.25em 0.5em;
  border-radius: 0.25rem;
}

/*noinspection CssUnusedSymbol */
.badge-info {
  background-color: #17a2b8;
  color: white;
}
/*noinspection CssUnusedSymbol */
.badge-success {
  background-color: #28a745;
  color: white;
}
/*noinspection CssUnusedSymbol */
.badge-warning {
  background-color: #ffc107;
  color: #212529;
}
/*noinspection CssUnusedSymbol */
.badge-danger {
  background-color: #dc3545;
  color: white;
}
/*noinspection CssUnusedSymbol */
.badge-primary {
  background-color: #007bff;
  color: white;
}
/*noinspection CssUnusedSymbol */
.badge-secondary {
  background-color: #6c757d;
  color: white;
}

.compliance-banner {
  border-left: 4px solid #ffc107;
}

.compliance-category {
  margin-bottom: 1rem;
}

.compliance-category:last-child {
  margin-bottom: 0;
}

.compliance-category-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.5rem 0;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.compliance-category-header i {
  width: 16px;
  text-align: center;
}

.compliance-pending-header {
  color: #ffc107;
}

.compliance-expired-header {
  color: #dc3545;
}

.compliance-expiring-header {
  color: #fd7e14;
}

.compliance-more {
  text-align: center;
  color: #888;
  font-size: 0.85rem;
  padding: 0.5rem 0;
  font-style: italic;
}

.email-auth-hidden-notice {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px;
  color: #888;
  font-size: 0.9rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px dashed rgba(255, 255, 255, 0.15);
}

.email-auth-hidden-notice i {
  font-size: 1.2rem;
  opacity: 0.6;
}

@media (max-width: 768px) {
  .stats-card {
    text-align: center;
    flex-direction: column;
  }

  .stats-icon {
    margin-right: 0;
    margin-bottom: 1rem;
  }

  .dashboard-header {
    flex-direction: column;
    align-items: flex-start;
  }

}
</style>
